import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import productCatalog from "../data/products";
import {
  DEFAULT_FREE_SHIPPING_THRESHOLD,
  DEFAULT_SHIPPING_RATE,
  DEFAULT_VARIANT_ID,
  LEGACY_CART_STORAGE_KEY,
  STORAGE_KEYS,
  calculateCartTotals,
  createCartLineId,
  findCatalogProduct,
  findProductVariant,
  getAvailableStock,
  getProductId,
  getProductVariants,
  getVariantId,
  hydrateCartEntries,
  normalizeCartEntries,
  normalizeQuantity,
  normalizeUserProfile,
  normalizeWishlist,
  readStorage,
  writeStorage,
} from "../utils/commerce";

export const CommerceContext = createContext(undefined);

const areCartEntriesEqual = (left, right) =>
  left.length === right.length &&
  left.every(
    (entry, index) =>
      entry.lineId === right[index]?.lineId &&
      entry.quantity === right[index]?.quantity
  );

const areListsEqual = (left, right) =>
  left.length === right.length &&
    left.every((value, index) => value === right[index]);

const FREE_SAMPLE_OPTIONS = new Set(["surprise", "smoky", "bright", "warm"]);
const normalizeFreeSample = (value) => FREE_SAMPLE_OPTIONS.has(value) ? value : "surprise";

const getStoredCart = (catalog) => {
  const storedCart = readStorage(STORAGE_KEYS.cart, undefined);
  const cart =
    storedCart === undefined
      ? readStorage(LEGACY_CART_STORAGE_KEY, [])
      : storedCart;

  return normalizeCartEntries(cart, catalog);
};

const parseAddRequest = (productOrId, variantOrOptions, quantity) => {
  let variantReference = variantOrOptions;
  let requestedQuantity = quantity;

  if (
    productOrId &&
    typeof productOrId === "object" &&
    productOrId.productId !== undefined &&
    variantOrOptions === undefined
  ) {
    variantReference = productOrId.variant ?? productOrId.variantId;
    requestedQuantity = productOrId.quantity ?? quantity;
  }

  if (
    variantOrOptions &&
    typeof variantOrOptions === "object" &&
    (variantOrOptions.variant !== undefined ||
      variantOrOptions.variantId !== undefined ||
      (variantOrOptions.quantity !== undefined &&
        variantOrOptions.price === undefined &&
        variantOrOptions.sku === undefined))
  ) {
    variantReference =
      variantOrOptions.variant ?? variantOrOptions.variantId ?? null;
    requestedQuantity = variantOrOptions.quantity ?? quantity;
  }

  return {
    variantReference,
    requestedQuantity: Math.max(1, normalizeQuantity(requestedQuantity || 1)),
  };
};

const resolveCartLineIds = (entries, productOrLine, variantOrId) => {
  const directLineId =
    productOrLine && typeof productOrLine === "object"
      ? productOrLine.lineId
      : productOrLine;
  const directMatch = entries.find(
    (entry) => entry.lineId === String(directLineId ?? "")
  );

  if (directMatch && variantOrId === undefined) {
    return [directMatch.lineId];
  }

  const productId = getProductId(productOrLine);

  if (!productId) {
    return [];
  }

  if (variantOrId !== undefined && variantOrId !== null) {
    return [createCartLineId(productId, variantOrId)];
  }

  return entries
    .filter((entry) => entry.productId === productId)
    .map((entry) => entry.lineId);
};

export function CommerceProvider({
  children,
  products = productCatalog,
  freeShippingThreshold = DEFAULT_FREE_SHIPPING_THRESHOLD,
  shippingRate = DEFAULT_SHIPPING_RATE,
}) {
  const catalog = useMemo(
    () => (Array.isArray(products) ? products : []),
    [products]
  );
  const [cartEntries, setCartEntries] = useState(() =>
    getStoredCart(catalog)
  );
  const [wishlistIds, setWishlistIds] = useState(() =>
    normalizeWishlist(readStorage(STORAGE_KEYS.wishlist, []), catalog)
  );
  const [user, setUser] = useState(() =>
    normalizeUserProfile(readStorage(STORAGE_KEYS.user, null))
  );
  const [freeSample, setFreeSampleState] = useState(() =>
    normalizeFreeSample(readStorage(STORAGE_KEYS.freeSample, "surprise"))
  );
  const [toast, setToast] = useState(null);
  const [activeDrawer, setActiveDrawer] = useState(null);
  const toastTimerRef = useRef(null);
  const toastIdRef = useRef(0);

  const hideToast = useCallback(() => {
    if (toastTimerRef.current !== null && typeof window !== "undefined") {
      window.clearTimeout(toastTimerRef.current);
    }

    toastTimerRef.current = null;
    setToast(null);
  }, []);

  const showToast = useCallback(
    (message, options = {}) => {
      const normalizedOptions =
        typeof options === "string" ? { type: options } : options;
      const text = String(message ?? "").trim();

      if (!text) {
        hideToast();
        return null;
      }

      if (toastTimerRef.current !== null && typeof window !== "undefined") {
        window.clearTimeout(toastTimerRef.current);
      }

      toastIdRef.current += 1;
      const nextToast = {
        id: toastIdRef.current,
        message: text,
        type: normalizedOptions.type || "info",
      };
      const duration = Math.max(
        0,
        Number(normalizedOptions.duration ?? 2600) || 0
      );

      setToast(nextToast);

      if (duration > 0 && typeof window !== "undefined") {
        toastTimerRef.current = window.setTimeout(() => {
          toastTimerRef.current = null;
          setToast(null);
        }, duration);
      }

      return nextToast.id;
    },
    [hideToast]
  );

  useEffect(
    () => () => {
      if (toastTimerRef.current !== null && typeof window !== "undefined") {
        window.clearTimeout(toastTimerRef.current);
      }
    },
    []
  );

  useEffect(() => {
    const normalizedCart = normalizeCartEntries(cartEntries, catalog);

    if (!areCartEntriesEqual(cartEntries, normalizedCart)) {
      setCartEntries(normalizedCart);
    }

    const normalizedWishlist = normalizeWishlist(wishlistIds, catalog);

    if (!areListsEqual(wishlistIds, normalizedWishlist)) {
      setWishlistIds(normalizedWishlist);
    }
  }, [catalog, cartEntries, wishlistIds]);

  useEffect(() => {
    writeStorage(STORAGE_KEYS.cart, cartEntries);
  }, [cartEntries]);

  useEffect(() => {
    writeStorage(STORAGE_KEYS.wishlist, wishlistIds);
  }, [wishlistIds]);

  useEffect(() => {
    writeStorage(STORAGE_KEYS.user, user);
  }, [user]);

  useEffect(() => {
    writeStorage(STORAGE_KEYS.freeSample, freeSample);
  }, [freeSample]);

  const setFreeSample = useCallback((value) => {
    setFreeSampleState(normalizeFreeSample(value));
  }, []);

  const cartItems = useMemo(
    () => hydrateCartEntries(cartEntries, catalog),
    [cartEntries, catalog]
  );

  const wishlistItems = useMemo(
    () =>
      wishlistIds
        .map((productId) => findCatalogProduct(catalog, productId))
        .filter(Boolean),
    [catalog, wishlistIds]
  );

  const totals = useMemo(
    () =>
      calculateCartTotals(
        cartItems,
        freeShippingThreshold,
        shippingRate
      ),
    [cartItems, freeShippingThreshold, shippingRate]
  );

  const addToCart = useCallback(
    (productOrId, variantOrOptions, quantity = 1) => {
      const product = findCatalogProduct(catalog, productOrId);

      if (!product) {
        showToast("This product is currently unavailable.", { type: "error" });
        return null;
      }

      const { variantReference, requestedQuantity } = parseAddRequest(
        productOrId,
        variantOrOptions,
        quantity
      );
      const variants = getProductVariants(product);
      const variant = findProductVariant(product, variantReference);

      if (variants.length > 0 && !variant) {
        showToast("Please choose an available option.", { type: "error" });
        return null;
      }

      const stock = getAvailableStock(product, variant);

      if (stock <= 0) {
        showToast("This option is currently out of stock.", { type: "error" });
        return null;
      }

      const productId = getProductId(product);
      const variantId = getVariantId(variant) || DEFAULT_VARIANT_ID;
      const lineId = createCartLineId(productId, variantId);

      setCartEntries((currentEntries) => {
        const existingEntry = currentEntries.find(
          (entry) => entry.lineId === lineId
        );
        const nextQuantity = normalizeQuantity(
          (existingEntry?.quantity || 0) + requestedQuantity,
          stock
        );

        if (existingEntry) {
          if (existingEntry.quantity === nextQuantity) {
            return currentEntries;
          }

          return currentEntries.map((entry) =>
            entry.lineId === lineId
              ? { ...entry, quantity: nextQuantity }
              : entry
          );
        }

        return [
          ...currentEntries,
          { lineId, productId, variantId, quantity: nextQuantity },
        ];
      });

      showToast(`${product.name} added to your cart.`, { type: "success" });
      return lineId;
    },
    [catalog, showToast]
  );

  const removeFromCart = useCallback(
    (productOrLine, variantOrId) => {
      const lineIds = resolveCartLineIds(
        cartEntries,
        productOrLine,
        variantOrId
      );

      if (lineIds.length === 0) {
        return false;
      }

      const lineIdSet = new Set(lineIds);
      setCartEntries((currentEntries) =>
        currentEntries.filter((entry) => !lineIdSet.has(entry.lineId))
      );
      showToast("Item removed from your cart.");
      return true;
    },
    [cartEntries, showToast]
  );

  const updateQuantity = useCallback(
    (productOrLine, variantOrQuantity, maybeQuantity) => {
      const usesExplicitVariant = maybeQuantity !== undefined;
      const lineIds = resolveCartLineIds(
        cartEntries,
        productOrLine,
        usesExplicitVariant ? variantOrQuantity : undefined
      );
      const lineId = lineIds.length === 1 ? lineIds[0] : null;
      const requestedQuantity = usesExplicitVariant
        ? maybeQuantity
        : variantOrQuantity;
      const existingEntry = cartEntries.find(
        (entry) => entry.lineId === lineId
      );

      if (!existingEntry) {
        return false;
      }

      const product = findCatalogProduct(catalog, existingEntry.productId);
      const variant = product
        ? findProductVariant(product, existingEntry.variantId)
        : null;
      const stock = product
        ? getAvailableStock(product, variant)
        : Number.POSITIVE_INFINITY;
      const nextQuantity = normalizeQuantity(requestedQuantity, stock);

      setCartEntries((currentEntries) =>
        nextQuantity === 0
          ? currentEntries.filter((entry) => entry.lineId !== lineId)
          : currentEntries.map((entry) =>
              entry.lineId === lineId
                ? { ...entry, quantity: nextQuantity }
                : entry
            )
      );

      return true;
    },
    [cartEntries, catalog]
  );

  const clearCart = useCallback(() => {
    setCartEntries([]);
    showToast("Your cart has been cleared.");
  }, [showToast]);

  const addToWishlist = useCallback(
    (productOrId) => {
      const product = findCatalogProduct(catalog, productOrId);

      if (!product) {
        return false;
      }

      const productId = getProductId(product);
      setWishlistIds((currentIds) =>
        currentIds.includes(productId)
          ? currentIds
          : [...currentIds, productId]
      );
      showToast(`${product.name} saved to your wishlist.`, {
        type: "success",
      });
      return true;
    },
    [catalog, showToast]
  );

  const removeFromWishlist = useCallback(
    (productOrId) => {
      const productId = getProductId(productOrId);

      if (!wishlistIds.includes(productId)) {
        return false;
      }

      setWishlistIds((currentIds) =>
        currentIds.filter((currentId) => currentId !== productId)
      );
      showToast("Item removed from your wishlist.");
      return true;
    },
    [showToast, wishlistIds]
  );

  const toggleWishlist = useCallback(
    (productOrId) =>
      wishlistIds.includes(getProductId(productOrId))
        ? removeFromWishlist(productOrId)
        : addToWishlist(productOrId),
    [addToWishlist, removeFromWishlist, wishlistIds]
  );

  const isWishlisted = useCallback(
    (productOrId) => wishlistIds.includes(getProductId(productOrId)),
    [wishlistIds]
  );

  const login = useCallback(
    (credentials = {}) => {
      const profile = normalizeUserProfile(
        credentials && typeof credentials === "object"
          ? credentials
          : { email: credentials }
      );

      setUser(profile);
      showToast("Welcome back.", { type: "success" });
      return Promise.resolve({ ok: true, user: profile });
    },
    [showToast]
  );

  const register = useCallback(
    (details = {}) => {
      const profile = normalizeUserProfile(
        details && typeof details === "object" ? details : { email: details }
      );

      setUser(profile);
      showToast("Your account is ready.", { type: "success" });
      return Promise.resolve({ ok: true, user: profile });
    },
    [showToast]
  );

  const updateProfile = useCallback(
    (changes = {}) => {
      const nextUser = normalizeUserProfile(changes, user);
      setUser(nextUser);
      showToast("Profile updated.", { type: "success" });
      return Promise.resolve({ ok: true, user: nextUser });
    },
    [showToast, user]
  );

  const logout = useCallback(() => {
    setUser(null);
    showToast("You have been signed out.");
    return Promise.resolve({ ok: true });
  }, [showToast]);

  const openDrawer = useCallback((drawerName) => {
    const name = String(drawerName ?? "").trim();

    if (name) {
      setActiveDrawer(name);
    }
  }, []);

  const closeDrawer = useCallback((drawerName) => {
    setActiveDrawer((currentDrawer) =>
      !drawerName || currentDrawer === drawerName ? null : currentDrawer
    );
  }, []);

  const toggleDrawer = useCallback((drawerName) => {
    const name = String(drawerName ?? "").trim();

    if (name) {
      setActiveDrawer((currentDrawer) =>
        currentDrawer === name ? null : name
      );
    }
  }, []);

  const closeAllDrawers = useCallback(() => setActiveDrawer(null), []);
  const openCartDrawer = useCallback(() => openDrawer("cart"), [openDrawer]);
  const closeCartDrawer = useCallback(
    () => closeDrawer("cart"),
    [closeDrawer]
  );

  const drawerState = useMemo(
    () => ({
      active: activeDrawer,
      isOpen: activeDrawer !== null,
      cart: activeDrawer === "cart",
      menu: activeDrawer === "menu",
      search: activeDrawer === "search",
      wishlist: activeDrawer === "wishlist",
    }),
    [activeDrawer]
  );

  const getProductById = useCallback(
    (productOrId) => findCatalogProduct(catalog, productOrId),
    [catalog]
  );

  const value = useMemo(
    () => ({
      products: catalog,
      getProductById,
      cart: cartItems,
      cartItems,
      cartEntries,
      addToCart,
      addCart: addToCart,
      removeFromCart,
      removeCart: removeFromCart,
      removeCartItem: removeFromCart,
      updateQuantity,
      updateCartQuantity: updateQuantity,
      clearCart,
      cartCount: totals.cartCount,
      subtotal: totals.subtotal,
      shipping: totals.shipping,
      freeShippingRemaining: totals.freeShippingRemaining,
      total: totals.total,
      qualifiesForFreeShipping: totals.qualifiesForFreeShipping,
      freeShippingThreshold,
      shippingRate,
      wishlist: wishlistIds,
      wishlistIds,
      wishlistItems,
      wishlistCount: wishlistIds.length,
      freeSample,
      setFreeSample,
      addToWishlist,
      addWishlist: addToWishlist,
      removeFromWishlist,
      removeWishlist: removeFromWishlist,
      toggleWishlist,
      isWishlisted,
      user,
      profile: user,
      isAuthenticated: Boolean(user),
      login,
      register,
      logout,
      updateProfile,
      toast,
      showToast,
      hideToast,
      dismissToast: hideToast,
      activeDrawer,
      drawer: drawerState,
      openDrawer,
      closeDrawer,
      toggleDrawer,
      closeAllDrawers,
      openCartDrawer,
      closeCartDrawer,
      isCartDrawerOpen: drawerState.cart,
      isMenuDrawerOpen: drawerState.menu,
      isSearchDrawerOpen: drawerState.search,
    }),
    [
      activeDrawer,
      addToCart,
      addToWishlist,
      cartEntries,
      cartItems,
      catalog,
      clearCart,
      closeAllDrawers,
      closeCartDrawer,
      closeDrawer,
      drawerState,
      freeShippingThreshold,
      freeSample,
      getProductById,
      hideToast,
      isWishlisted,
      login,
      logout,
      openDrawer,
      openCartDrawer,
      register,
      removeFromCart,
      removeFromWishlist,
      shippingRate,
      setFreeSample,
      showToast,
      toast,
      toggleDrawer,
      toggleWishlist,
      totals,
      updateProfile,
      updateQuantity,
      user,
      wishlistIds,
      wishlistItems,
    ]
  );

  return (
    <CommerceContext.Provider value={value}>
      {children}
    </CommerceContext.Provider>
  );
}

export default CommerceContext;
